import { CdkVirtualScrollViewport } from "@angular/cdk/scrolling";
import { Component, Input, OnInit, ViewChild, OnDestroy, ChangeDetectorRef } from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { ModalController, NavController, Platform, ToastController } from "@ionic/angular";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { QueryDocumentSnapshot } from "firebase/firestore";
import * as _ from "lodash-es";
import { Subscription, combineLatest, interval, skipWhile, take } from "rxjs";
import { GetHeaderItem, GetHeaderTitle, GetHeaderType, ResetHeaderState } from "src/app/shared/actions/utility.actions";
import { TypeCustomFields } from "src/app/shared/enums/type-custom-fields";
import { TypeHeader } from "src/app/shared/enums/type-header";
import { TypeModule } from "src/app/shared/enums/type-module";
import {
	ICustomField,
	IEvent,
	IEventUser,
	IFullCustomField,
	IGroup,
	IModule,
	IModuleCustomField,
	ISheet
} from "src/app/shared/interfaces";
import {
	ICustomFieldData,
	IFilterOptions,
	IFilteredItemFormat
} from "src/app/shared/interfaces/custom-fields.interfaces";
import { IFavoriteFolder } from "src/app/shared/interfaces/folders.interfaces";
import { IModuleUpdatedSettings } from "src/app/shared/interfaces/modules.interfaces";
import { getCurrentEventUser } from "src/app/shared/selectors/auth.selectors";
import { getCurrentEvent } from "src/app/shared/selectors/events.selectors";
import {
	getBaseCustomFields,
	getGroupsByOrder,
	getModulesCustomsFieldsOfModule,
	getSpecificFavoriteFolder
} from "src/app/shared/selectors/generics-modules-data.selectors";
import { getSpecificModule } from "src/app/shared/selectors/modules.selectors";
import { selectRouteNestedParams, selectUrl } from "src/app/shared/selectors/router.selectors";
import {
	AnalyticsService,
	EventUsersService,
	FavoritesService,
	FirestoreService,
	LocalStorageService,
	SheetsService,
	UtilityService
} from "src/app/shared/services";
import { LogFuncTrace } from "src/app/shared/utils-func/debug-decorators";
import { environment } from "src/environments/environment";

@Component({
	selector: "app-sheets-favorites",
	templateUrl: "./sheets-favorites.component.html",
	styleUrls: ["./sheets-favorites.component.scss"],
	standalone: false
})
export class SheetsFavoriteComponent implements OnInit, OnDestroy {
	@ViewChild(CdkVirtualScrollViewport) virtualScroll: CdkVirtualScrollViewport;
	@Input() sheets: ISheet[] = [];
	@Input() module: IModule;
	@Input() mode: "normal" | "favorites";

	subscriptions: Subscription[] = [];
	analyticsArraySub: { name: string; sub: Subscription }[] = [];

	loader: boolean = true;
	limit: number = 30;
	currentSize: number = 0;

	searchOpen: boolean = false;
	searchText: string = "";

	totalSheets: number = 0;
	lastQuery: {
		start: QueryDocumentSnapshot<ISheet>;
		last: QueryDocumentSnapshot<ISheet>;
		docsSnapshots: QueryDocumentSnapshot<ISheet>[];
		docsDatas: ISheet[];
	} = null;

	eventId: string;
	event: IEvent;
	moduleId: string;
	sheetModuleId: string;
	moduleUpdatedSettings: IModuleUpdatedSettings;
	eventUser: IEventUser | null;
	filteredSheets: ISheet[] = [];
	filteredSheetsInTwo: ISheet[][] = [];
	groupIds: string[];
	groups: IGroup[] = [];
	customFields: IFullCustomField[] = [];
	tagCustomFields: IFullCustomField[] = [];

	filterItems: IFilteredItemFormat[];
	typeModule = TypeModule;
	typeOrder: string;

	showNoResultImage: boolean = false;

	allowFilterTag: boolean = false;
	isMobile = window.innerWidth < 768 ? true : false;
	selectedFilters: IFilteredItemFormat[] = [];
	selectedDatas: IFilterOptions[] = [];
	notSelectedFilters: IFilteredItemFormat[];

	groupedLetters: any;
	itemSize: number = 97;
	height: number = 873;

	dontBelongAnyGroups: boolean = false;

	init: boolean = false;
	initSheetsDatas: boolean = false;
	navigationSaved: { moduleId: string; lastIndex: number }[] = [];
	currentLanguage: string = environment.platform.defaultLanguage;
	searchValue: string = "";

	filtersCollapseState: { uid: string; collapsed: boolean }[] = [];

	resultsSaved: [ICustomField[], IModuleCustomField[]] = [[], []];

	searchDatas: any[] = [];
	folderId: any;
	folder: IFavoriteFolder;
	favoriteModule: IModule;
	sheetModule: IModule;
	sheetFavoritesState: any = {};
	scanning: boolean = false;

	constructor(
		public platform: Platform,
		private SAnalytics: AnalyticsService,
		private store: Store,
		private SSheets: SheetsService,
		private SEventUsers: EventUsersService,
		private SFirestore: FirestoreService,
		private toastCtrl: ToastController,
		private navCtrl: NavController,
		private snackBar: MatSnackBar,
		private STranslate: TranslateService,
		private modalController: ModalController,
		private SUtility: UtilityService,
		public SFavorite: FavoritesService,
		private localStorageService: LocalStorageService,
		private cdr: ChangeDetectorRef
	) {}

	ionViewWillEnter() {
		this.store.dispatch(GetHeaderType({ payload: TypeHeader.FAVORITE_SHEETS_FOLDERS }));
		this.currentLanguage = this.STranslate.currentLang;

		this.subscriptions.push(
			this.STranslate.onLangChange.subscribe((lang) => {
				this.currentLanguage = lang.lang;
			})
		);
		this.subscriptions.push(
			this.store
				.select(selectUrl)
				.pipe(take(1))
				.subscribe(() => {
					this.store
						.select(selectRouteNestedParams)
						.pipe(take(1))
						.subscribe((params) => {
							this.eventId = params.eventId;
							this.init = false;
							this.initSheetsDatas = false;

							this.moduleId = params.moduleId;
							this.folderId = params.folderId;

							this.getEvent();
							this.getFolder();

							this.SAnalytics.accessToFavoriteFolder(this.eventId, this.moduleId, this.folderId);
						});
				})
		);
	}

	ionViewDidEnter() {
		this.subscriptions.push(
			interval(100)
				.pipe(
					skipWhile(() => this.loader || !this.virtualScroll),
					take(1)
				)
				.subscribe(() => {
					if (this.virtualScroll) {
						const nav = this.navigationSaved.find((navMod) => navMod.moduleId === this.moduleId);
						this.virtualScroll.scrollToIndex(nav ? nav.lastIndex : 0, "auto");
						this.init = true;
					}
				})
		);
	}

	ngOnInit() {
		this.subscriptions.push(
			this.SFavorite.favoriteFolderScanQrSubject.subscribe((scanQr) => {
				if (scanQr) {
					this.scanQr();
				}
			})
		);
	}

	@LogFuncTrace
	ionViewWillLeave() {
		this.sheets = [];
		this.folder = null;
		this.favoriteModule = null;
		this.eventUser = null;
		this.filteredSheets = [];
		this.lastQuery = null;
		this.initSheetsDatas = false;
		this.eventUser = null;
		this.subscriptions.forEach((sub) => sub.unsubscribe());
		this.store.dispatch(ResetHeaderState(null));
		this.loader = true;
		this.init = false;
		this.resetFilter();
	}

	@LogFuncTrace
	ngOnDestroy() {
		this.folder = null;
		this.favoriteModule = null;
		this.eventUser = null;
		this.sheets = [];
		this.filteredSheets = [];
		this.lastQuery = null;
		this.initSheetsDatas = false;

		this.subscriptions.forEach((sub) => sub.unsubscribe());
		this.analyticsArraySub.forEach((analyticData) => analyticData.sub.unsubscribe());
	}

	getEvent() {
		this.subscriptions.push(
			this.store.select(getCurrentEvent).subscribe((event) => {
				this.event = event;
			})
		);
	}

	getFolder() {
		this.subscriptions.push(
			this.store.select(getSpecificFavoriteFolder(this.folderId)).subscribe((folder) => {
				if (folder && !_.isEqual(this.folder, folder)) {
					this.folder = folder;
					this.sheetModuleId = this.folder.moduleLinkedId;
					if (this.folder) {
						this.store.dispatch(GetHeaderTitle({ payload: this.folder.name }));
						this.getModuleAndEventUser();
						this.getGroups();
						this.getCustomFields();
					}
				}
			})
		);
	}

	getModuleAndEventUser() {
		this.subscriptions.push(
			combineLatest([
				this.store.select(getCurrentEventUser),
				this.store.select(getSpecificModule(this.sheetModuleId)),
				this.store.select(getSpecificModule(this.moduleId))
			]).subscribe({
				next: (results) => {
					const eventUserComing = results[0];
					const sheetModule = results[1];
					const favoriteModule = results[2];

					let needsUpdate = false;

					if (!_.isEqual(this.sheetModule, sheetModule)) {
						this.sheetModule = sheetModule;
						needsUpdate = true;
					}

					if (!_.isEqual(this.favoriteModule, favoriteModule)) {
						this.favoriteModule = favoriteModule;
						needsUpdate = true;
					}

					if (!_.isEqual(this.eventUser, eventUserComing)) {
						this.eventUser = eventUserComing;
						this.initSheetsDatas = false;
						needsUpdate = true;
					}

					if (needsUpdate && !this.initSheetsDatas) {
						this.buildFavoritesState();
						this.getSheets();
					}

					if (this.folder && this.favoriteModule) {
						this.store.dispatch(GetHeaderTitle({ payload: this.folder.name }));
						this.store.dispatch(GetHeaderItem({ payload: this.favoriteModule }));
						this.store.dispatch(GetHeaderType({ payload: TypeHeader.FAVORITE_SHEETS_FOLDERS }));
					}
				},
				error: (error) => {
					console.error("Error getting module and event user:", error);
					this.loader = false;
				}
			})
		);
	}

	getCustomFields() {
		if (!this.sheetModuleId) return;

		this.subscriptions.push(
			combineLatest([
				this.store.select(getBaseCustomFields),
				this.store.select(getModulesCustomsFieldsOfModule(this.sheetModuleId))
			])
				.pipe(take(1))
				.subscribe((results) => {
					if (!_.isEqual(results, this.resultsSaved)) {
						this.resultsSaved = results;
						this.customFields = [];
						const baseCustomFields = results[0];
						const moduleCustomFields = results[1];

						if (moduleCustomFields.length > 0) {
							moduleCustomFields.forEach((customField) => {
								const baseCustomFieldCorresponding = baseCustomFields.find(
									(custField) => custField.uid === customField.uid
								);

								if (baseCustomFieldCorresponding) {
									this.customFields.push({
										baseSettings: baseCustomFieldCorresponding,
										moduleSettings: customField,
										fieldDatas: null
									});
								}
							});
						}
						this.tagCustomFields = this.customFields.filter(
							(custom) => custom.moduleSettings && custom.moduleSettings.canBeTag
						);
					}
				})
		);
	}

	initFilterData() {
		this.filterItems = [];
		this.filtersCollapseState = [];
	}

	computeReachableDatas() {
		this.filterItems?.forEach((item) => {
			item.values.forEach((data) => {
				data.isReachable = true;
			});
		});
	}

	getSheets() {
		if (!this.eventUser && !this.localStorageService.getItem("guestFavorites")) {
			this.sheets = [];
			this.filteredSheets = [];
			this.totalSheets = 0;
			this.loader = false;
			this.initSheetsDatas = true;
			this.cdr.markForCheck();
			return;
		}

		this.initSheetsDatas = true;
		this.loader = true;

		const favoriteIds = this.eventUser
			? (this.eventUser.favorites ?? [])
			: (this.localStorageService.getItem<string[]>("guestFavorites") ?? []);

		if (favoriteIds.length === 0) {
			this.sheets = [];
			this.filteredSheets = [];
			this.totalSheets = 0;
			this.loader = false;
			this.cdr.markForCheck();
			return;
		}

		this.subscriptions.push(
			this.SSheets.getSpecificSheetsForEvent(this.eventId, favoriteIds)
				.pipe(take(1))
				.subscribe({
					next: (sheets: ISheet[]) => {
						this.sheets = sheets?.filter((sheet) => sheet.moduleId === this.folder.moduleLinkedId);
						this.totalSheets = this.sheets.length;
						this.buildSheets();
						this.buildFavoritesState();
						this.loader = false;
						this.cdr.markForCheck();
					},
					error: (error) => {
						console.error("Error fetching favorite sheets:", error);
						this.sheets = [];
						this.filteredSheets = [];
						this.totalSheets = 0;
						this.loader = false;
						this.cdr.markForCheck();
					}
				})
		);
	}

	buildSheets() {
		if (this.searchValue) {
			const searchTerm = this.SUtility.removeAccents(this.searchValue).toLocaleLowerCase();
			this.filteredSheets = this.sheets.filter(
				(sheet) =>
					this.SUtility.removeAccents(sheet.name).toLocaleLowerCase().includes(searchTerm) ||
					(sheet.identifier &&
						this.SUtility.removeAccents(sheet.identifier).toLocaleLowerCase().includes(searchTerm))
			);
		} else {
			this.filteredSheets = [...this.sheets];
		}

		this.filteredSheets.sort((a, b) => a.queryName.localeCompare(b.queryName));

		this.groupedLetters = this.filteredSheets.reduce((groups, sheet) => {
			const letter = sheet.queryName.charAt(0).toUpperCase();
			if (!Object.values(groups).includes(letter)) {
				const uid = sheet.uid;
				groups[uid] = letter;
			}
			return groups;
		}, {});

		this.currentSize = this.filteredSheets.length;
		this.getListOnTwoBloc();
		this.loader = false;
		this.showNoResultImage =
			this.filteredSheets.length === 0 && (this.searchValue !== "" || this.anyFilterIsActivated());

		this.cdr.markForCheck();
	}

	canShowFilterBloc(): boolean {
		return false;
	}

	getListOnTwoBloc() {
		let count = 0;
		let index = 0;
		const newFilteredSheetsOnTwo = [];
		this.filteredSheets.forEach((sheet) => {
			if (count === 2) {
				index++;
				count = 0;
			}
			if (!newFilteredSheetsOnTwo[index]) {
				newFilteredSheetsOnTwo[index] = [];
			}
			newFilteredSheetsOnTwo[index].push(sheet);
			count++;
		});
		if (!_.isEqual(this.filteredSheetsInTwo, newFilteredSheetsOnTwo)) {
			this.filteredSheetsInTwo = newFilteredSheetsOnTwo;
		}
	}

	getGroups() {
		this.subscriptions.push(
			this.store.select(getGroupsByOrder("asc")).subscribe((groups) => {
				this.groups = groups;
				this.groupIds = this.groups.map((group) => group.uid);
			})
		);
	}

	getCustomFieldValue(customFieldData: ICustomFieldData): string {
		if (!customFieldData || !customFieldData.field) return "";

		const fullCustomField = this.customFields.find((cf) => cf.baseSettings.uid === customFieldData.uid);
		if (!fullCustomField) return "";

		const type = fullCustomField.baseSettings.type;

		if (
			(type === TypeCustomFields.TEXT || type === TypeCustomFields.SELECT) &&
			customFieldData.field.multiLanguageText
		) {
			return customFieldData.field.multiLanguageText[this.currentLanguage] || "";
		} else if ((type === TypeCustomFields.URL || type === TypeCustomFields.EMAIL) && customFieldData.field.text) {
			return customFieldData.field.text;
		} else if (
			type === TypeCustomFields.NUMERIC &&
			customFieldData.field.numeric !== undefined &&
			customFieldData.field.numeric !== -1
		) {
			return customFieldData.field.numeric.toString();
		} else if (type === TypeCustomFields.MULTI_SELECT && customFieldData.field.multiLanguageSelectArray) {
			const values = customFieldData.field.multiLanguageSelectArray
				.map((option) => option?.[this.currentLanguage])
				.filter(Boolean);
			return values.join(", ");
		}

		return "";
	}

	sheetHeader() {
		return null;
	}

	searchBar(ev) {
		const value = ev.target?.value ?? "";
		this.loader = true;
		if (value.length >= 1) {
			this.searchValue = value;
			this.buildSheets();
		} else {
			this.resetFilter();
		}
	}

	filterSheet(ev, filterId: string) {
		this.filteredSheets = [...this.sheets];
		this.getListOnTwoBloc();
		this.computeReachableDatas();
		this.cdr.markForCheck();
	}

	filterIsActivated(filter: IFilteredItemFormat): boolean {
		return filter?.values.some((data) => data.isSelected) ?? false;
	}

	anyFilterIsActivated(): boolean {
		return this.selectedFilters?.length > 0;
	}

	getSheetTags(sheet: ISheet): ICustomFieldData[] {
		if (!sheet.customFields || !this.customFields) return [];

		return sheet.customFields
			.filter((sheetCustomField) => {
				if (!sheetCustomField) return false;
				const computedCustomField = this.customFields.find(
					(cf) => cf.baseSettings.uid === sheetCustomField.uid
				);
				return (
					computedCustomField &&
					computedCustomField.moduleSettings.canBeTag &&
					this.hasValue(sheetCustomField, computedCustomField.baseSettings.type)
				);
			})
			.sort((a, b) => this.getCustomFieldOrder(a) - this.getCustomFieldOrder(b));
	}

	private hasValue(fieldData: ICustomFieldData, type: TypeCustomFields): boolean {
		if (!fieldData || !fieldData.field) return false;
		switch (type) {
			case TypeCustomFields.TEXT:
			case TypeCustomFields.SELECT:
				return !!fieldData.field.multiLanguageText?.[this.currentLanguage]?.trim();
			case TypeCustomFields.URL:
			case TypeCustomFields.EMAIL:
				return !!fieldData.field.text?.trim();
			case TypeCustomFields.NUMERIC:
				return (
					fieldData.field.numeric !== undefined &&
					fieldData.field.numeric !== null &&
					fieldData.field.numeric !== -1
				);
			case TypeCustomFields.MULTI_SELECT:
				return !!fieldData.field.multiLanguageSelectArray?.some(
					(option) => !!option?.[this.currentLanguage]?.trim()
				);
			default:
				return false;
		}
	}

	isSheetOrModuleFieldsVisibilityHidden(sheet: ISheet, key: string, type: "baseFields" | "customFields"): boolean {
		return false;
	}

	getCustomFieldOrder(customField: ICustomFieldData): number {
		const found = this.customFields?.find((cus) => cus.baseSettings.uid === customField.uid);
		return found ? found.moduleSettings.order : 999;
	}

	resetFilter() {
		this.searchValue = "";
		this.loader = true;
		this.buildSheets();
		this.showNoResultImage = false;
	}

	// async openMobileFilterModal() {
	// }

	getCustomFieldType(customFieldId: string): number | undefined {
		return this.customFields?.find((cus) => cus.baseSettings.uid === customFieldId)?.baseSettings.type;
	}

	navigateTo(sheet: ISheet) {
		this.navCtrl.navigateForward(`/event/${sheet.eventId}/profile/${sheet.moduleId}/${sheet.uid}`);
	}

	limitScroll: boolean = false;
	scrolledIndexChanged(index: number, _el: any) {
		const nav = this.navigationSaved.find((navMod) => navMod.moduleId === this.moduleId);
		if (this.init) {
			if (!nav) {
				this.navigationSaved.push({ moduleId: this.moduleId, lastIndex: index });
			} else {
				nav.lastIndex = index;
			}
		}
	}

	collapseFilter(filterId: string) {
		const filterState = this.filtersCollapseState?.find((filter) => filter.uid === filterId);
		if (filterState) {
			filterState.collapsed = !filterState.collapsed;
		}
	}

	getFilterCollapseState(filterId: string): boolean {
		return this.filtersCollapseState?.find((filter) => filter.uid === filterId)?.collapsed ?? false;
	}

	getCorrespondingGroup(groupId: string) {
		return this.groups?.find((gr) => gr.uid === groupId);
	}

	async removeFromFavorite(sheetUid: string, event: MouseEvent): Promise<void> {
		event.stopPropagation();
		const originalSheets = [...this.sheets];

		try {
			this.sheets = this.sheets.filter((sheet) => sheet.uid !== sheetUid);
			this.buildSheets();
			this.sheetFavoritesState[sheetUid] = false;
			this.cdr.markForCheck();

			if (this.eventUser) {
				const currentFavorites = this.eventUser.favorites ? [...this.eventUser.favorites] : [];
				const updatedFavorites = currentFavorites.filter((fav) => fav !== sheetUid);

				if (!_.isEqual(currentFavorites, updatedFavorites)) {
					await this.SEventUsers.updatePartOfEventUser(
						this.eventUser.eventId,
						this.eventUser.moduleId,
						this.eventUser.uid,
						{ favorites: updatedFavorites }
					);
				} else {
					console.warn("Sheet already removed or not found in user favorites.");
				}
			} else {
				const guestFavorites = this.localStorageService.getItem<string[]>("guestFavorites") ?? [];
				const updatedGuestFavorites = guestFavorites.filter((fav) => fav !== sheetUid);

				if (!_.isEqual(guestFavorites, updatedGuestFavorites)) {
					this.localStorageService.setItem("guestFavorites", updatedGuestFavorites);
				} else {
					console.warn("Sheet already removed or not found in guest favorites.");
				}
			}

			this.snackBar.open(this.STranslate.instant("snackbar.removed_from_favorites"), null, {
				duration: 3000,
				panelClass: ["success-snackbar"]
			});
		} catch (error) {
			console.error("🚀 ~ removeFromFavorite (Sheet) ~ error:", error);
			this.sheets = originalSheets;
			this.buildSheets();
			this.sheetFavoritesState[sheetUid] = true;
			this.cdr.markForCheck();

			this.snackBar.open(this.STranslate.instant("snackbar.error_occured"), null, {
				duration: 3000,
				panelClass: ["error-snackbar"]
			});
		}
	}

	buildFavoritesState() {
		const favoriteIds = this.eventUser
			? (this.eventUser.favorites ?? [])
			: (this.localStorageService.getItem<string[]>("guestFavorites") ?? []);

		const newState = {};
		this.sheets.forEach((sheet) => {
			newState[sheet.uid] = favoriteIds.includes(sheet.uid);
		});
		this.sheetFavoritesState = newState;
		this.cdr.markForCheck();
	}

	scanQr() {
		try {
			if (!this.scanning) {
				this.loader = true;
				this.scanning = true;

				// For web scanning
				if (!this.isMobile) {
					return;
				}

				this.SFavorite.perfomScanQr(this.event, this.module, this.eventUser, this.customFields);

				this.scanning = false;
			}
		} catch (error) {
			this.scanning = false;

			console.error("🚀 ~ scanQr ~ error:", error);
			this.snackBar.open(this.STranslate.instant("snackbar.error_occured"), null, {
				duration: 3000,
				panelClass: ["error-snackbar"]
			});
		}
	}

	trackByUidPair(index: number, sheetPair: ISheet[]) {
		return sheetPair.map((sheet) => sheet.uid).join("-");
	}

	trackByUid(index: number, sheet: ISheet) {
		return sheet.uid;
	}
}
